<template>
  <div id="production-table">
    <div class="table-header d-flex flex-column">
      <div class="d-flex align-center w-100">
        <v-switch
          class="mr-4"
          color="primary"
          v-model="showDead"
          hide-details
          label="Dead"
          @update:model-value="loadQuoteIndexes"
        ></v-switch>
        <v-switch
          color="primary"
          v-model="filterQuotes"
          label="Just mine"
          hide-details
          @update:model-value="loadQuoteIndexes"
        ></v-switch>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          class="mr-2"
          :loading="csvGenerating"
          :disabled="loading || csvSourceQuotes.length === 0"
          @click="downloadCSV"
        >
          <v-icon start>mdi-download</v-icon>
          Download CSV
        </v-btn>
        <v-btn variant="outlined" color="primary" @click="openVersionCheckDialog">
          <v-icon start>mdi-eye</v-icon>
          Version Check
        </v-btn>
      </div>
      <div class="d-flex align-center w-100 mt-1">
        <DateRangePicker default="lastTwelveMonths" @change="dateRangeChanged" />
        <v-spacer />
        <v-text-field
          v-model="tableSearch"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          :clearable="true"
        >
        </v-text-field>
      </div>
    </div>
    <v-data-table
      :items="filteredQuotes"
      :headers="headers"
      :search="tableSearch"
      :sort-by="[{ key: 'dateCreated', order: 'desc' }]"
      :loading="loading"
      :no-data-text="noDataText"
      :items-per-page="10"
      must-sort
      fixed-header
    >
      <template v-slot:item="props">
        <tr>
          <td>{{ $utils.relativeTime(props.item.dateCreated) }}</td>
          <td>{{ props.item.customerEmail }}</td>
          <td>{{ props.item.companyName }}</td>
          <td>{{ props.item.projectName }}</td>
          <td>{{ props.item.creatorEmail }}</td>
          <td v-if="hasVersionAllowOrder">
            <div v-if="props.item.versionsWithAllowOrder && props.item.versionsWithAllowOrder.length > 0">
              <div v-for="(version, index) in props.item.versionsWithAllowOrder" :key="index" class="py-1">
                <span>{{ version.title }} - </span>
                <span v-if="version.total">{{ formatCurrency(version.total, props.item.region) }}</span>
                <span v-else class="text-disabled">No pricing</span>
              </div>
            </div>
            <span v-else class="text-disabled">No versions</span>
          </td>
          <td v-if="hasVersionReqCheck">
            <div v-if="props.item.versionsReqCheck && props.item.versionsReqCheck.length > 0">
              <div v-for="(version, index) in props.item.versionsReqCheck" :key="index" class="py-1">
                <span>{{ version.title }} - </span>
                <span v-if="version.total">{{ formatCurrency(version.total, props.item.region) }}</span>
                <span v-else class="text-disabled">No pricing</span>
              </div>
            </div>
            <span v-else class="text-disabled">No versions</span>
          </td>
          <td class="text-right">
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon size="small" class="mr-2" @click="edit(props.item.id)" v-bind="activatorProps"
                  >mdi-pencil</v-icon
                >
              </template>
              <span>Edit quote info</span>
            </v-tooltip>
            <v-tooltip v-if="$store.state.user.permissions.admin" location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon
                  size="small"
                  class="mr-2"
                  @click="
                    () => {
                      selectedQuote = props.item.id
                      deleteDialog = true
                    }
                  "
                  v-bind="activatorProps"
                  >mdi-delete</v-icon
                >
              </template>
              <span>Delete quote index</span>
            </v-tooltip>
            <v-tooltip v-if="!showDead" location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon size="small" class="mr-2" @click="onThumbDown(props.item)" v-bind="activatorProps"
                  >mdi-thumb-down</v-icon
                >
              </template>
              <span>Kill quote</span>
            </v-tooltip>
            <v-tooltip v-else location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon size="small" class="mr-2" @click="resurrectFromDead(props.item)" v-bind="activatorProps"
                  >mdi-thumb-up</v-icon
                >
              </template>
              <span>Revive quote</span>
            </v-tooltip>
            <v-tooltip location="top" :open-delay="500">
              <template v-slot:activator="{ props: activatorProps }">
                <v-icon size="small" @click="view(props.item.id)" v-bind="activatorProps">mdi-clipboard-text</v-icon>
              </template>
              <span>View quote page</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Dead Reason Dialogue -->
    <v-dialog v-model="deadReasonVisible" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6"> Mark as dead </v-card-title>
        <v-card-text>
          <v-select v-model="deadReason" :items="deadReasons" label="Select Reason"> </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deadReasonVisible = false">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="setAsDead">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialogue -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card>
        <v-card-title class="bg-warning text-white text-h6">Delete Quote</v-card-title>
        <v-card-text class="pt-6">
          <p>Are you sure you want to delete the quote?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="warning" variant="flat" @click="deleteQuote(selectedQuote)">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Version Check Dialogue -->
    <v-dialog v-model="versionCheckDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Version Check Filters</v-card-title>
        <v-card-text class="pt-6">
          <v-switch
            v-if="$store.state.user.permissions.admin"
            color="primary"
            v-model="tempHasVersionAllowOrder"
            label="Place Order Enabled"
            hide-details
            class="mb-4"
          ></v-switch>
          <v-switch
            v-if="$store.state.user.permissions.admin"
            color="primary"
            v-model="tempHasVersionReqCheck"
            label="Requires Check"
            hide-details
          ></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="cancelVersionCheck">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="applyVersionCheck">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Quote Dialogue -->
    <v-dialog v-model="editDialog" persistent width="600">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Edit Quote</v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="editForm" validate-on="blur">
            <v-text-field
              v-model="editForm.customerEmail"
              label="Customer Email"
              :rules="[$rules.required('email'), $rules.email('email')]"
              required
            ></v-text-field>
            <v-combobox
              v-model="editForm.creatorEmail"
              :items="creatorEmailOptions"
              label="Creator Email"
              :rules="[$rules.required('creator email'), $rules.email('creator email')]"
              required
              class="mt-4"
            ></v-combobox>
            <v-autocomplete
              v-model="editForm.customerType"
              :items="customerTypes"
              label="Customer Type"
              :rules="[$rules.required('customer type')]"
              required
              class="mt-4"
            ></v-autocomplete>
            <v-text-field
              v-if="projectDetailsRequired"
              v-model="editForm.companyName"
              label="Company Name"
              :rules="[$rules.required('company name')]"
              required
              class="mt-4"
            ></v-text-field>
            <v-text-field
              v-if="projectDetailsRequired"
              v-model="editForm.projectName"
              label="Project Name"
              :rules="[$rules.required('project name')]"
              required
              class="mt-4"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelEdit">Cancel</v-btn>
          <v-btn variant="flat" color="accent" @click="saveQuoteEdit" :disabled="submitting">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AnalyticsSettings from '@/js/analytics-settings'
import DateRangePicker from '@/components/ui/DateRangePicker'
import { auth, db } from '@/js/db'
import { collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore'

// Smaller quotes aren't useful for sales reporting, so they're excluded from the CSV.
const CSV_MIN_QUOTE_VALUE = 1000

export default {
  name: 'Quotes',
  components: { DateRangePicker },
  data() {
    return {
      userEmail: auth.currentUser.email,
      quotes: [],
      loading: true,
      activeDateRange: null,
      selectedQuote: null,
      deleteDialog: false,
      deadReasonVisible: false,
      deadReason: null,
      deadReasons: [
        'Too expensive',
        'Lead time too long',
        'Vanished 👻',
        'Went with competitor',
        'Decided to do something different',
        'Project cancelled',
        'Process too complicated',
        'Installing non-ikea kitchen'
      ],
      filterQuotes: true,
      hasVersionAllowOrder: false,
      hasVersionReqCheck: false,
      tempHasVersionAllowOrder: false,
      tempHasVersionReqCheck: false,
      versionCheckDialog: false,
      showDead: false,
      csvGenerating: false,
      //
      tableSearch: '',
      // Cached totals for performance
      cachedVersionTotals: { uk: 0, eu: 0, us: 0, total: 0 },
      cachedCheckReqTotals: { uk: 0, eu: 0, us: 0, total: 0 },
      // Edit dialog state
      editDialog: false,
      editingQuoteId: null,
      editForm: {
        customerEmail: '',
        creatorEmail: '',
        customerType: '',
        companyName: '',
        projectName: ''
      },
      submitting: false,
      customerTypes: [
        { text: 'Home Owner', value: 'Home Owner', projectDetailsRequired: false },
        { text: 'Architect', value: 'Architect', projectDetailsRequired: true },
        { text: 'Interior Designer', value: 'Interior Designer', projectDetailsRequired: true },
        { text: 'Property Developer', value: 'Property Developer', projectDetailsRequired: true },
        { text: 'Contractor', value: 'Contractor', projectDetailsRequired: true }
      ]
    }
  },
  computed: {
    headers() {
      const baseHeaders = [
        {
          title: 'Date Created',
          sortable: true,
          key: 'dateCreated'
        },
        {
          title: 'Customer',
          sortable: false,
          key: 'customerEmail'
        },
        {
          title: 'Company Name',
          sortable: false,
          key: 'companyName'
        },
        {
          title: 'Project Name',
          sortable: false,
          key: 'projectName'
        },
        {
          title: 'Creator',
          sortable: false,
          key: 'creatorEmail'
        }
      ]

      // Add the total column if hasVersionAllowOrder is enabled
      if (this.hasVersionAllowOrder) {
        baseHeaders.push({
          title: this.getVersionsHeaderText(),
          sortable: false,
          key: 'versionsWithAllowOrder',
          align: 'start'
        })
      }

      // Add the check required column if hasVersionReqCheck is enabled
      if (this.hasVersionReqCheck) {
        baseHeaders.push({
          title: this.getCheckReqHeaderText(),
          sortable: false,
          key: 'versionsReqCheck',
          align: 'start'
        })
      }

      // Add actions column
      baseHeaders.push({
        title: 'Actions',
        sortable: false,
        align: 'end',
        key: null
      })

      return baseHeaders
    },
    filteredQuotes() {
      return this.quotes.filter((q) => {
        return this.filterQuotes ? q.creatorEmail === this.userEmail : true
      })
    },
    csvSourceQuotes() {
      const search = (this.tableSearch || '').trim().toLowerCase()
      const searchable = ['customerEmail', 'companyName', 'projectName', 'creatorEmail']
      const quotes = search
        ? this.filteredQuotes.filter((q) =>
            searchable.some((k) =>
              String(q[k] || '')
                .toLowerCase()
                .includes(search)
            )
          )
        : this.filteredQuotes
      return quotes
        .slice()
        .sort((a, b) => this.dateCreatedMillis(b.dateCreated) - this.dateCreatedMillis(a.dateCreated))
    },
    noDataText() {
      return this.loading ? '' : 'Nothing to display'
    },
    projectDetailsRequired() {
      if (!this.editForm.customerType) return false
      const selectedType = this.customerTypes.find((ct) => ct.value === this.editForm.customerType)
      return selectedType ? selectedType.projectDetailsRequired : false
    },
    creatorEmailOptions() {
      return AnalyticsSettings.quoteTeam.map((u) => u.email)
    }
  },
  watch: {
    'editForm.customerType'(newVal) {
      const selectedType = this.customerTypes.find((ct) => ct.value === newVal)
      if (selectedType && !selectedType.projectDetailsRequired) {
        this.editForm.companyName = 'N/A'
        this.editForm.projectName = 'N/A'
      } else if (selectedType && selectedType.projectDetailsRequired) {
        // Clear the N/A values when switching to a customer type that requires details
        if (this.editForm.companyName === 'N/A') this.editForm.companyName = ''
        if (this.editForm.projectName === 'N/A') this.editForm.projectName = ''
      }
    },
    filteredQuotes: {
      handler() {
        // Debounce to avoid excessive calls during rapid filter changes
        clearTimeout(this._totalsTimeout)
        this._totalsTimeout = setTimeout(() => {
          this.updateCachedTotals()
        }, 100)
      },
      immediate: false
    }
  },
  methods: {
    formatCurrency(amount, region) {
      const formattedAmount = this.$numeral(amount).format('0,0.00')
      if (region === 'uk') return `£${formattedAmount}`
      if (region === 'eu') return `€${formattedAmount}`
      return `$${formattedAmount}`
    },
    getCheckReqHeaderText() {
      const totals = this.formatRegionTotals(this.cachedCheckReqTotals)
      return totals ? `Requires Check (Total: ${totals})` : 'Requires Check'
    },

    getCheckReqTotalSum() {
      if (!this.filteredQuotes || this.filteredQuotes.length === 0) {
        return { uk: 0, eu: 0, us: 0, total: 0 }
      }

      return this.filteredQuotes.reduce(
        (totals, quote) => {
          if (!quote.versionsReqCheck || quote.versionsReqCheck.length === 0) {
            return totals
          }

          const quoteSum = quote.versionsReqCheck.reduce((sum, version) => {
            return sum + (version.total || 0)
          }, 0)

          // Accumulate by region
          if (quote.region === 'uk') {
            totals.uk += quoteSum
          } else if (quote.region === 'eu') {
            totals.eu += quoteSum
          } else if (quote.region === 'us') {
            totals.us += quoteSum
          }
          totals.total += quoteSum

          return totals
        },
        { uk: 0, eu: 0, us: 0, total: 0 }
      )
    },

    getVersionsHeaderText() {
      const totals = this.formatRegionTotals(this.cachedVersionTotals)
      return totals ? `Place Order Enabled (Total: ${totals})` : 'Place Order Enabled'
    },
    formatRegionTotals(totals) {
      const parts = []
      if (totals.uk > 0) parts.push(`£${this.$numeral(totals.uk).format('0,0.00')}`)
      if (totals.eu > 0) parts.push(`€${this.$numeral(totals.eu).format('0,0.00')}`)
      if (totals.us > 0) parts.push(`$${this.$numeral(totals.us).format('0,0.00')}`)
      return parts.join(' / ')
    },
    getTotalSum() {
      if (!this.filteredQuotes || this.filteredQuotes.length === 0) {
        return { uk: 0, eu: 0, us: 0, total: 0 }
      }

      return this.filteredQuotes.reduce(
        (totals, quote) => {
          if (!quote.versionsWithAllowOrder || quote.versionsWithAllowOrder.length === 0) {
            return totals
          }

          const quoteSum = quote.versionsWithAllowOrder.reduce((sum, version) => {
            return sum + (version.total || 0)
          }, 0)

          // Accumulate by region
          if (quote.region === 'uk') {
            totals.uk += quoteSum
          } else if (quote.region === 'eu') {
            totals.eu += quoteSum
          } else if (quote.region === 'us') {
            totals.us += quoteSum
          }
          totals.total += quoteSum

          return totals
        },
        { uk: 0, eu: 0, us: 0, total: 0 }
      )
    },
    dateCreatedMillis(dateCreated) {
      if (!dateCreated) return 0
      try {
        return this.$utils.ensureFirestoreTimestamp(dateCreated).toDate().getTime()
      } catch (e) {
        return 0
      }
    },
    escapeCSV(value) {
      if (value === null || value === undefined) return ''
      const stringValue = String(value)
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    },
    async downloadCSV() {
      if (this.csvGenerating) return

      const indexRows = this.csvSourceQuotes
      if (indexRows.length === 0) {
        this.$store.commit('openSnackbar', { snackbarText: 'Nothing to export' })
        return
      }

      this.csvGenerating = true

      try {
        // quoteIndex has no versions/pricing, so pull the full quote docs for the same range.
        const [quotesByID, zoneNames] = await Promise.all([
          this.fetchFullQuotes(indexRows.map((q) => q.id)),
          this.fetchShippingZoneNames()
        ])
        const ordersByVersion = await this.fetchOrdersForQuotes(quotesByID)

        const headers = [
          'Date Created',
          'Order Number',
          'Order Date',
          'Customer Email',
          'Delivery Location',
          'Delivery Postcode',
          'Region',
          'Quote Value',
          'Value Status',
          'Status'
        ]

        // Quotes below the threshold (and any without a usable value) are left out entirely.
        const rows = indexRows
          .map((indexRow) => {
            const quote = quotesByID[indexRow.id]
            return { indexRow, picked: quote ? this.pickCsvVersion(indexRow.id, quote, ordersByVersion) : null }
          })
          .filter(({ picked }) => picked && picked.total >= CSV_MIN_QUOTE_VALUE)

        if (rows.length === 0) {
          this.$store.commit('openSnackbar', {
            snackbarText: `No quotes of ${CSV_MIN_QUOTE_VALUE} or more to export`
          })
          return
        }

        const lines = rows.map(({ indexRow, picked }) => {
          const deliveryLocation = picked.version.deliveryLocation
          // Only paid rows carry an order, so order number and postcode are blank elsewhere.
          const contactDetails = (picked.order && picked.order.customerContactDetails) || {}

          return [
            this.escapeCSV(this.$utils.convertDate(indexRow.dateCreated)),
            this.escapeCSV((picked.order && picked.order.orderNumber) || ''),
            this.escapeCSV(picked.order ? this.$utils.convertDate(picked.order.dateCreated) : ''),
            this.escapeCSV(indexRow.customerEmail || ''),
            this.escapeCSV(deliveryLocation ? zoneNames[deliveryLocation] || deliveryLocation : ''),
            this.escapeCSV(contactDetails.deliveryPostcode || ''),
            this.escapeCSV((indexRow.region || '').toUpperCase()),
            picked.total,
            this.escapeCSV(picked.status),
            picked.paid ? 'PAID' : 'UNPAID'
          ].join(',')
        })

        const csvContent = [headers.join(','), ...lines].join('\n')

        const startDate = this.$moment(this.activeDateRange[0]).format('DD MMM YYYY')
        const endDate = this.$moment(this.activeDateRange[1]).format('DD MMM YYYY')
        const filename = `Quotes - ${startDate} to ${endDate}.csv`

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        this.$store.commit('openSnackbar', { snackbarText: 'Error building CSV: ' + error.message })
      } finally {
        this.csvGenerating = false
      }
    },
    async fetchFullQuotes(ids) {
      const idSet = new Set(ids)

      const constraints = [
        where('dateCreated', '>=', this.activeDateRange[0].startOf('day').toDate()),
        where('dateCreated', '<=', this.activeDateRange[1].endOf('day').toDate())
      ]

      if (this.filterQuotes) {
        constraints.push(where('creatorEmail', '==', this.userEmail))
      }

      const quotesSnap = await getDocs(query(collection(db, 'quotes'), ...constraints))

      const quotesByID = {}
      quotesSnap.docs.forEach((snap) => {
        if (idSet.has(snap.id)) quotesByID[snap.id] = snap.data()
      })
      return quotesByID
    },
    async fetchShippingZoneNames() {
      const zonesSnap = await getDocs(collection(db, 'shippingZones'))
      const names = {}
      zonesSnap.docs.forEach((snap) => {
        names[snap.id] = snap.data().name
      })
      return names
    },
    // Orders are keyed `quoteID|versionID` so a version's order can be looked up directly.
    async fetchOrdersForQuotes(quotesByID) {
      const wanted = []
      Object.keys(quotesByID).forEach((quoteID) => {
        const orders = quotesByID[quoteID].orders || {}
        Object.keys(orders).forEach((versionID) => {
          if (orders[versionID] && orders[versionID].orderID) {
            wanted.push({ key: `${quoteID}|${versionID}`, orderID: orders[versionID].orderID })
          }
        })
      })

      const ordersByVersion = {}
      const batchSize = 30

      for (let i = 0; i < wanted.length; i += batchSize) {
        const batch = wanted.slice(i, i + batchSize)
        const batchDocs = await Promise.all(batch.map((w) => getDoc(doc(db, 'orders', w.orderID))))

        batchDocs.forEach((snap, index) => {
          if (snap.exists()) ordersByVersion[batch[index].key] = snap.data()
        })
      }

      return ordersByVersion
    },
    isOrderPaid(order) {
      if (!order) return false
      if (order.balanceOutstanding) return false
      // FOC and manually cleared orders never carry matching payment rows.
      if (order.foc || order.balanceCleared) return true

      const payments = order.payments || []
      if (payments.length === 0) return false

      const total = this.$utils.toFixedNumber(order.total || 0, 2)
      const paidTotal = payments.reduce((sum, p) => sum + this.$utils.toFixedNumber(p.amount || 0, 2), 0)
      return this.$utils.toFixedNumber(paidTotal, 2) >= total
    },
    versionTotal(version) {
      return this.$utils.toFixedNumber((version.pricing && version.pricing.total) || 0, 2)
    },
    largestByTotal(candidates) {
      return candidates.slice().sort((a, b) => b.total - a.total)[0]
    },
    pickCsvVersion(quoteID, quote, ordersByVersion) {
      const versions = Array.isArray(quote.versions) ? quote.versions : []
      if (versions.length === 0) return null

      const paid = versions
        .map((version) => ({ version, order: ordersByVersion[`${quoteID}|${version.id}`] }))
        .filter((entry) => this.isOrderPaid(entry.order))
        .map((entry) => ({
          version: entry.version,
          order: entry.order,
          status: 'Order Paid',
          paid: true,
          total: this.$utils.toFixedNumber(entry.order.total || 0, 2)
        }))
      if (paid.length > 0) return this.largestByTotal(paid)

      const checkComplete = versions
        .filter((version) => version.readyToCheck && version.versionCheckComplete)
        .map((version) => ({ version, status: 'Check Complete', paid: false, total: this.versionTotal(version) }))
      if (checkComplete.length > 0) return this.largestByTotal(checkComplete)

      const requiresCheck = versions
        .filter((version) => version.readyToCheck && !version.versionCheckComplete)
        .map((version) => ({ version, status: 'Requires Check', paid: false, total: this.versionTotal(version) }))
      if (requiresCheck.length > 0) return this.largestByTotal(requiresCheck)

      if (versions.length === 1) {
        return { version: versions[0], status: 'Only Version', paid: false, total: this.versionTotal(versions[0]) }
      }

      return this.largestByTotal(
        versions.map((version) => ({
          version,
          status: 'Most Expensive Version',
          paid: false,
          total: this.versionTotal(version)
        }))
      )
    },
    dateRangeChanged(range) {
      // If range is empty, null, or invalid, don't load data
      if (!range || !range[0] || !range[1]) {
        this.activeDateRange = null
        this.quotes = []
        return
      }

      this.activeDateRange = range
      this.quotes = []
      this.loadQuoteIndexes()
    },
    async loadQuoteIndexes() {
      // Don't load if no date range is selected
      if (!this.activeDateRange || !this.activeDateRange[0] || !this.activeDateRange[1]) {
        return
      }

      this.loading = true

      // If filters are active, use optimized approach
      if (this.hasVersionAllowOrder || this.hasVersionReqCheck) {
        // Process both filters in parallel
        const [payableQuotes, checkReqQuotes] = await Promise.all([
          this.hasVersionAllowOrder ? this.getPayableQuotesOptimized() : Promise.resolve([]),
          this.hasVersionReqCheck ? this.getCheckReqQuotesOptimized() : Promise.resolve([])
        ])

        // Merge and deduplicate results
        this.quotes = this.mergeQuotes(payableQuotes, checkReqQuotes, [])
      } else {
        // Standard query when no filters active
        const constraints = [
          where('dead', '==', this.showDead),
          where('dateCreated', '>=', this.activeDateRange[0].startOf('day').toDate()),
          where('dateCreated', '<=', this.activeDateRange[1].endOf('day').toDate())
        ]

        if (this.filterQuotes) {
          constraints.push(where('creatorEmail', '==', this.userEmail))
        }

        const quotesSnap = await getDocs(query(collection(db, 'quoteIndex'), ...constraints))
        this.quotes = quotesSnap.docs.map((snap) => ({
          id: snap.id,
          ...snap.data()
        }))
      }

      this.loading = false

      // Calculate totals once after loading
      this.updateCachedTotals()
    },
    async getPayableQuotesOptimized() {
      // Query for quotes that have payableVersion field populated
      const constraints = [
        where('dateCreated', '>=', this.activeDateRange[0].startOf('day').toDate()),
        where('dateCreated', '<=', this.activeDateRange[1].endOf('day').toDate())
      ]

      if (this.filterQuotes) {
        constraints.push(where('creatorEmail', '==', this.userEmail))
      }

      const quotesSnap = await getDocs(query(collection(db, 'quotes'), ...constraints))

      // Process in chunks to avoid blocking
      const quotesToFetch = []
      const versionDataMap = new Map()
      const docs = quotesSnap.docs

      // Process documents in smaller chunks to prevent blocking
      const chunkSize = 50
      for (let i = 0; i < docs.length; i += chunkSize) {
        const chunk = docs.slice(i, i + chunkSize)

        // Allow browser to breathe between chunks
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, 0))
        }

        chunk.forEach((snap) => {
          const quote = snap.data()
          if (quote.payableVersion && Object.keys(quote.payableVersion).length > 0) {
            quotesToFetch.push(snap.id)
            // Extract version data from payableVersion object
            const versions = Object.values(quote.payableVersion).map((v) => ({
              title: v.title,
              total: v.total
            }))
            versionDataMap.set(snap.id, versions)
          }
        })
      }

      // Batch fetch quoteIndex documents
      if (quotesToFetch.length === 0) return []

      // Limit batch size to prevent too many concurrent requests
      const batchSize = 30
      const results = []

      for (let i = 0; i < quotesToFetch.length; i += batchSize) {
        const batch = quotesToFetch.slice(i, i + batchSize)
        const batchPromises = batch.map((id) => getDoc(doc(db, 'quoteIndex', id)))

        const batchDocs = await Promise.all(batchPromises)

        batchDocs.forEach((snap, index) => {
          if (snap.exists()) {
            const quoteId = batch[index]
            results.push({
              id: quoteId,
              ...snap.data(),
              versionsWithAllowOrder: versionDataMap.get(quoteId)
            })
          }
        })
      }

      return results
    },

    async getCheckReqQuotesOptimized() {
      // Query for quotes that have checkReqVersions field populated
      const constraints = [
        where('dateCreated', '>=', this.activeDateRange[0].startOf('day').toDate()),
        where('dateCreated', '<=', this.activeDateRange[1].endOf('day').toDate())
      ]

      if (this.filterQuotes) {
        constraints.push(where('creatorEmail', '==', this.userEmail))
      }

      const quotesSnap = await getDocs(query(collection(db, 'quotes'), ...constraints))

      // Filter for quotes that have checkReqVersions with content
      const quotesToFetch = []
      const versionDataMap = new Map()
      const docs = quotesSnap.docs

      // Process documents in smaller chunks to prevent blocking
      const chunkSize = 50
      for (let i = 0; i < docs.length; i += chunkSize) {
        const chunk = docs.slice(i, i + chunkSize)

        // Allow browser to breathe between chunks
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, 0))
        }

        chunk.forEach((snap) => {
          const quote = snap.data()
          if (quote.checkReqVersions && Object.keys(quote.checkReqVersions).length > 0) {
            quotesToFetch.push(snap.id)
            // Extract version data from checkReqVersions object
            const versions = Object.values(quote.checkReqVersions).map((v) => ({
              title: v.title,
              total: v.total
            }))
            versionDataMap.set(snap.id, versions)
          }
        })
      }

      // Batch fetch quoteIndex documents
      if (quotesToFetch.length === 0) return []

      // Limit batch size to prevent too many concurrent requests
      const batchSize = 30
      const results = []

      for (let i = 0; i < quotesToFetch.length; i += batchSize) {
        const batch = quotesToFetch.slice(i, i + batchSize)
        const batchPromises = batch.map((id) => getDoc(doc(db, 'quoteIndex', id)))

        const batchDocs = await Promise.all(batchPromises)

        batchDocs.forEach((snap, index) => {
          if (snap.exists()) {
            const quoteId = batch[index]
            results.push({
              id: quoteId,
              ...snap.data(),
              versionsReqCheck: versionDataMap.get(quoteId)
            })
          }
        })
      }

      return results
    },

    mergeQuotes(payableQuotes, checkReqQuotes, allQuotes) {
      // If no filters are active, return all quotes
      if (!this.hasVersionAllowOrder && !this.hasVersionReqCheck) {
        return allQuotes
      }

      // Create a map to merge quotes by ID
      const quoteMap = new Map()

      // Add payable quotes
      payableQuotes.forEach((quote) => {
        quoteMap.set(quote.id, quote)
      })

      // Add or merge check required quotes
      checkReqQuotes.forEach((quote) => {
        if (quoteMap.has(quote.id)) {
          // Merge the versionsReqCheck into existing quote
          quoteMap.get(quote.id).versionsReqCheck = quote.versionsReqCheck
        } else {
          // Add new quote
          quoteMap.set(quote.id, quote)
        }
      })

      return Array.from(quoteMap.values())
    },
    updateCachedTotals() {
      this.cachedVersionTotals = this.getTotalSum()
      this.cachedCheckReqTotals = this.getCheckReqTotalSum()
    },
    async edit(id) {
      this.editingQuoteId = id

      // Fetch the full quote from the quotes collection
      const quoteDoc = await getDoc(doc(db, 'quotes', id))

      if (quoteDoc.exists()) {
        const quoteData = quoteDoc.data()

        // Populate edit form with current values
        this.editForm = {
          customerEmail: quoteData.customerEmail || '',
          creatorEmail: quoteData.creatorEmail || '',
          customerType: quoteData.customerType || '',
          companyName: quoteData.companyName || '',
          projectName: quoteData.projectName || ''
        }

        this.editDialog = true
      } else {
        this.$store.commit('openSnackbar', { snackbarText: 'Quote not found' })
      }
    },
    cancelEdit() {
      // Clear validation errors without touching the values — the reset below does those.
      this.$refs.editForm?.resetValidation()
      // Close dialog
      this.editDialog = false
      // Reset form state
      this.editForm = {
        customerEmail: '',
        creatorEmail: '',
        customerType: '',
        companyName: '',
        projectName: ''
      }
      this.editingQuoteId = null
    },
    async saveQuoteEdit() {
      // Validate the form
      const valid = await this.$utils.validateForm(this.$refs.editForm)

      if (!valid || this.submitting) {
        return
      }

      this.submitting = true

      try {
        // Update only the quotes collection
        await updateDoc(doc(db, 'quotes', this.editingQuoteId), {
          customerEmail: this.editForm.customerEmail,
          creatorEmail: this.editForm.creatorEmail,
          customerType: this.editForm.customerType,
          companyName: this.editForm.companyName,
          projectName: this.editForm.projectName
        })

        // Also update quoteIndex collection for consistency in the table view
        await updateDoc(doc(db, 'quoteIndex', this.editingQuoteId), {
          customerEmail: this.editForm.customerEmail,
          creatorEmail: this.editForm.creatorEmail,
          companyName: this.editForm.companyName,
          projectName: this.editForm.projectName
        })

        // Close dialog and show success message
        this.editDialog = false
        this.submitting = false
        this.editingQuoteId = null

        // Reset form
        this.editForm = {
          customerEmail: '',
          creatorEmail: '',
          customerType: '',
          companyName: '',
          projectName: ''
        }
        this.$refs.editForm?.resetValidation()

        this.$store.commit('openSnackbar', { snackbarText: 'Quote updated successfully' })

        // Reload the quotes to reflect changes
        if (this.activeDateRange && this.activeDateRange[0] && this.activeDateRange[1]) {
          await this.loadQuoteIndexes()
        }
      } catch (error) {
        this.submitting = false
        this.$store.commit('openSnackbar', { snackbarText: 'Error updating quote: ' + error.message })
      }
    },
    view(id) {
      if (this.hasVersionAllowOrder || this.hasVersionReqCheck) {
        const route = this.$router.resolve({ name: 'quoteEdit', params: { quoteID: id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: 'quoteEdit', params: { quoteID: id } })
      }
    },
    deleteQuote(id) {
      this.deleteDialog = false
      deleteDoc(doc(db, 'quoteIndex', id))
        .then(() => {
          // Drop it from the local list so the table updates without a refetch/reload.
          const i = this.quotes.findIndex((q) => q.id === id)
          if (i !== -1) this.quotes.splice(i, 1)
          this.$store.commit('openSnackbar', { snackbarText: 'Quote Deleted' })
        })
        .catch((err) => {
          this.$store.commit('openSnackbar', { snackbarText: 'Error: ' + err })
        })
    },
    onThumbDown(quote) {
      this.selectedQuote = quote
      this.deadReasonVisible = true
    },
    async setAsDead() {
      const quote = this.selectedQuote
      await updateDoc(doc(db, 'quoteIndex', quote.id), {
        dead: true,
        deadReason: this.deadReason
      })

      // Reflect the change locally so the row's status/icons update without a reload.
      quote.dead = true
      quote.deadReason = this.deadReason

      this.deadReason = null
      this.deadReasonVisible = false
      this.selectedQuote = null
    },
    async resurrectFromDead(quote) {
      await updateDoc(doc(db, 'quoteIndex', quote.id), {
        dead: false,
        deadReason: null
      })

      // Reflect the change locally so the row's status/icons update without a reload.
      quote.dead = false
      quote.deadReason = null
    },
    openVersionCheckDialog() {
      // Initialize temp values with current values
      this.tempHasVersionAllowOrder = this.hasVersionAllowOrder
      this.tempHasVersionReqCheck = this.hasVersionReqCheck
      this.versionCheckDialog = true
    },
    applyVersionCheck() {
      this.hasVersionAllowOrder = this.tempHasVersionAllowOrder
      this.hasVersionReqCheck = this.tempHasVersionReqCheck
      this.versionCheckDialog = false
      this.loadQuoteIndexes()
    },
    cancelVersionCheck() {
      this.versionCheckDialog = false
    },
    versionDateFormatter(row) {
      return this.$utils.relativeTime(row.versions[row.versions.length - 1].date)
    },
    customSort(items, index, isDescending) {
      items.sort((a, b) => {
        if (!isDescending) {
          return a.dateCreated < b.dateCreated ? 1 : -1
        } else {
          return a.dateCreated < b.dateCreated ? -1 : 1
        }
      })
      return items
    }
  }
}
</script>
